import HTTPClient from 'services/HTTPClient';
import { TOPICS } from 'utils'
import Spinner from './Spinner';
import { useQuery } from '@tanstack/react-query';
import React, { ReactNode, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router';

interface Props {
    topic: typeof TOPICS[0],
    user_id?: number,
    after?: Date,
    onPercentObtained: Function
    getDetail?: (quizes: QuizResponse) => ReactNode
}

export type QuestionData = {
    id: number;
    attributes: {
        question: string;
        option_1: string;
        option_2: string;
        option_3: string;
        option_4: string;
        answer: string;
        user_answer: string;
        is_correct: null | true | false;
        createdAt: string;
        updatedAt: string;
    };
};

export type QuizData = {
    id: number;
    attributes: {
        topic: string;
        status: 'COMPLETE';
        createdAt: string;
        updatedAt: string;
        completedAt: string;
        questions: {
            data: QuestionData[]
        };
        score: number // custom
    };
};

export type QuizResponse = {
    data: QuizData[]
}



const useQuiz = (topic, user_id: number | null = null, after: Date | null = null) => {
    let url = `/api/quiz-sessions?filters[status][$eq]=COMPLETE&filters[topic][$eq]=${topic}&populate=questions`
    if (user_id) {
        url += `&user=${user_id}`
    }
    if (after) {
        url += `&filters[completedAt][$gte]=${after.toISOString()}`
    }
    return useQuery<QuizResponse>(['quizSessions', topic, user_id, after], () => HTTPClient.get().getJSONResult(url));
};

const computeScore = (quizes: QuizResponse): [number, number] => {
    let totalScore = 0
    let finalScore = 0;
    for (let i = 0; i < quizes.data.length; i++) {
        const quiz = quizes.data[i];
        quiz.attributes.score = 0
        for (let j = 0; j < quiz.attributes.questions.data.length; j++) {
            const question = quiz.attributes.questions.data[j];
            totalScore += 1;
            if (question.attributes.user_answer === question.attributes.answer || question.attributes.is_correct) {
                quiz.attributes.score = (quiz.attributes.score || 0) + 1
            }
        }
        finalScore += quiz.attributes.score

    }

    return [finalScore, totalScore]
}

export default function QuizTopicCard({ topic, user_id, after, onPercentObtained, getDetail = () => <></> }: Props) {
    const { data: quizes, isLoading, error } = useQuiz(topic.topic, user_id, after);
    const [finalScore, totalScore] = quizes ? computeScore(quizes!) : [0, 0]
    let navigate = useNavigate();

    const percent = totalScore ? parseInt(((finalScore / totalScore) * 100).toString()) : 0

    useEffect(() => {
        onPercentObtained(topic.label, percent);
    }, [topic.label, percent]);

    console.log("Rendering quiz topic card")

    return (
        <div
            className='card mb-4 px-5 py-5 c-pointer'
            style={{ backgroundColor: topic.color, maxHeight: "85vh", overflow: "auto" }}
            onClick={() => navigate(`/quiz/${topic.topic}`)}
        >
            <div className="between">
                <div>
                    <h5 className='is-size-5 has-text-weight-semibold'>
                        {topic.label}
                    </h5>
                    {isLoading
                        ?
                        <Spinner className='mt-2' />
                        : <>
                            {`${quizes?.data.length} quizes taken`}
                            {" / "}
                            {`${(quizes?.data.length || 0) * 3} questions`}

                        </>}
                </div>
                <div className='center'>
                    <h4 className='is-size-4'>
                        <strong className='mr-3'>
                            <span title={`Final Score: ${finalScore}\nTotal Score: ${totalScore}`}>
                                {percent} %
                            </span>
                        </strong>
                    </h4>
                    <div className='topic-card-icon'>
                        {<topic.icon fill="grey" width={30} height={30} />}
                    </div>
                </div>
            </div>
            {quizes && getDetail && getDetail?.(quizes)}
        </div>
    )
}